import { Component, Input, forwardRef, ViewChild, ElementRef, Optional, Self } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule, NgControl } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';

import { HasValueDirective } from '@shared/directives/has-value-directive';

import { AppIcons } from '@core/utilities/icons';

@Component({
  selector: 'app-core-input',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    FontAwesomeModule,
    NzFormModule,
    NzInputModule,
    HasValueDirective
  ],
  templateUrl: './core-input.component.html',
  styleUrl: './core-input.component.scss',
  // providers: [
  //   {
  //     provide: NG_VALUE_ACCESSOR,
  //     useExisting: forwardRef(() => CoreInputComponent),
  //     multi: true
  //   }
  // ]
})
export class CoreInputComponent implements ControlValueAccessor {

  constructor(
    @Optional() @Self() public ngControl: NgControl
  ) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }
  @Input() label: string = '';
  @Input() helperText: string = '';
  @Input() type: string = 'text';
  @Input() placeholder: string = '';
  @Input() disabled: boolean = false;
  @Input() isLockable: boolean = false;
  @Input() isLocked: boolean = false;
  @Input() min: string = '';
  @Input() max: string = '';
  @Input() restrictionPattern: RegExp | string | null = null;
  @Input() isCurrency: boolean = false;
  @Input() isError: boolean = false;
  @Input() errorMessages: Record<string, string> = {};
  @Input() maxlength?: number;
  @Input() minlength?: number;

  @ViewChild('inputRef') inputElement!: ElementRef<HTMLInputElement>;

  value: any = '';
  // isLocked: boolean = false;
  isFocused: boolean = false;

  icons: typeof AppIcons = AppIcons;

  onChange = (_: any) => {};
  onTouched = () => {};

  // =========================
  // VALUE ACCESSOR
  // =========================

  writeValue(value: any): void {
    if (this.isCurrency && value) {
      this.value = this.formatCurrency(value.toString());
    } else {
      this.value = value ?? '';
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // =========================
  // INPUT LOGIC
  // =========================

  onInput(event: Event): void {
    let value = (event.target as HTMLInputElement).value;

    // Currency formatting
    if (this.isCurrency) {
      const rawValue = value.replace(/\D/g, '');

      if (rawValue) {
        value = this.formatCurrency(rawValue);
        (event.target as HTMLInputElement).value = value;

        this.value = value;
        this.onChange(Number(rawValue));
      } else {
        this.value = '';
        this.onChange(null);
      }
      return;
    }

    // Pattern restriction
    if (this.restrictionPattern) {
      const pattern = typeof this.restrictionPattern === 'string'
        ? new RegExp(this.restrictionPattern, 'g')
        : this.restrictionPattern;

      value = value.replace(pattern, '');
      (event.target as HTMLInputElement).value = value;
    }

    this.value = value;
    this.onChange(value);
  }

  onFocus(): void {
    this.isFocused = true;
  }

  handleBlur(): void {
    this.isFocused = false;
    this.onTouched();

    if (this.isLockable) {
      this.isLocked = true;
    }
  }

  toggleLock(): void {
    if (this.disabled) {
      return; // Si el form lo deshabilitó, no se puede desbloquear manualmente
    }

    this.isLocked = !this.isLocked;

    if (!this.isLocked) {
      setTimeout(() => {
        this.inputElement?.nativeElement?.focus();
      });
    }
  }

  get isDisabled(): boolean {
    return this.disabled || this.isLocked;
  }

  private formatCurrency(value: string): string {
    const number = Number(value.replace(/\D/g, ''));
    return new Intl.NumberFormat('es-CO').format(number);
  }

  get showError(): boolean {
    const control = this.ngControl?.control;
    return !!(control && control.invalid && (control.dirty || control.touched));
  }

  get showHelper(): boolean {
    return !!(this.helperText && !this.showError);
  }

  get firstErrorKey(): string | null {
    const control = this.ngControl?.control;
    if (!control || !control.errors || !this.showError) return null;

    const errors = control.errors;

    if (errors['required']) return 'required';
    if (errors['minlength']) return 'minlength';
    if (errors['maxlength']) return 'maxlength';
    if (errors['pattern']) return 'pattern';

    return Object.keys(errors)[0] ?? null;
  }
}
